import Typography from "@vapor/react-extended/ExtendedTypography";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Stack,
} from "@vapor/react-material";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePermission } from "../../../../../core/permissions/usePermission";

export interface FulfilmentDetailFooterPlaceholderProps {
    isFulfilmentCloseable: boolean;
    isFulfilmentReopenable: boolean;
    isTaxPayerDissociable: boolean;
    loading: boolean;
    onClickCloseFulfilmentButton: () => unknown;
    onClickReopenFulfilmentButton: () => unknown;
    onClickUnlinkTaxPayerButton: () => unknown;
}

export const FulfilmentDetailFooter = ({
    isFulfilmentCloseable,
    isFulfilmentReopenable,
    isTaxPayerDissociable,
    loading,
    onClickCloseFulfilmentButton,
    onClickReopenFulfilmentButton,
    onClickUnlinkTaxPayerButton,
}: FulfilmentDetailFooterPlaceholderProps): ReactElement => {
    const { t } = useTranslation();
    const canCloseFulfilment = usePermission("fulfilmentClose");
    const canReopenFulfilment = usePermission("fulfilmentReopen");
    const canUnlinkTaxPayer = usePermission("taxPayerUnlink");
    const [isOpenCloseFulfilmentDialog, setIsOpenCloseFulfilmentDialog] =
        useState(false);
    const [isOpenReopenFulfilmentDialog, setIsOpenReopenFulfilmentDialog] =
        useState(false);
    const [isOpenUnlinkTaxPayerDialog, setIsOpenUnlinkTaxPayerDialog] =
        useState(false);

    const onClickAbortDialogs = (
        _: React.MouseEvent<unknown, MouseEvent>,
        reason?: "backdropClick" | "escapeKeyDown",
    ) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            // Set 'open' to false, however you would do that with your particular code.
            setIsOpenCloseFulfilmentDialog(false);
            setIsOpenReopenFulfilmentDialog(false);
            setIsOpenUnlinkTaxPayerDialog(false);
        }
    };

    return (
        <Stack
            direction="row"
            spacing={4}
            p={4}
            alignItems="center"
            justifyContent="flex-end"
        >
            <Stack direction="row" spacing={3}>
                {/* I rami sono scelti SOLO dallo stato dell'adempimento: un
                    permesso negato nasconde l'azione del ramo corrente, non fa
                    scivolare sul ramo dello stato successivo (che mostrerebbe
                    "Adempimento completato" su uno non completato, o perfino un
                    "Riapri" attivo). */}
                {isTaxPayerDissociable ? (
                    <Stack direction="row">
                        {canUnlinkTaxPayer && (
                            <Button
                                onClick={() =>
                                    setIsOpenUnlinkTaxPayerDialog(true)
                                }
                                disabled={false}
                                variant="contained"
                            >
                                <Typography ml={1}>
                                    {t(
                                        "views.fulfilment_detail.components.fulfilment_detail_footer.buttons.unlink_fulfilment",
                                    )}
                                </Typography>
                            </Button>
                        )}
                    </Stack>
                ) : (
                    <Stack direction="row">
                        {isFulfilmentCloseable ? (
                            <Stack direction="row">
                                {canCloseFulfilment && (
                                    <Button
                                        onClick={() =>
                                            setIsOpenCloseFulfilmentDialog(true)
                                        }
                                        disabled={false}
                                        variant="contained"
                                    >
                                        <Typography ml={1}>
                                            {t(
                                                "views.fulfilment_detail.components.fulfilment_detail_footer.buttons.close_fulfilment",
                                            )}
                                        </Typography>
                                    </Button>
                                )}
                            </Stack>
                        ) : (
                            <Stack direction="row">
                                {isFulfilmentReopenable ? (
                                    canReopenFulfilment && (
                                        <Button
                                            onClick={() =>
                                                setIsOpenReopenFulfilmentDialog(
                                                    true,
                                                )
                                            }
                                            disabled={false}
                                            variant="contained"
                                        >
                                            <Typography ml={1}>
                                                {t(
                                                    "views.fulfilment_detail.components.fulfilment_detail_footer.buttons.reopen_fulfilment",
                                                )}
                                            </Typography>
                                        </Button>
                                    )
                                ) : (
                                    <Button disabled={true} variant="contained">
                                        <Typography ml={1}>
                                            {t(
                                                "views.fulfilment_detail.components.fulfilment_detail_footer.buttons.completed_fulfilment",
                                            )}
                                        </Typography>
                                    </Button>
                                )}
                            </Stack>
                        )}
                    </Stack>
                )}

                <Dialog
                    open={isOpenUnlinkTaxPayerDialog}
                    onClose={onClickAbortDialogs}
                    aria-labelledby="unlink-tax-payer-dialog-title"
                    aria-describedby="unlink-tax-payer-dialog-description"
                >
                    <DialogTitle id="unlink-tax-payer-dialog-title">
                        {t(
                            "views.fulfilment_detail.components.fulfilment_detail_footer.unlink_fulfilment_dialog.title",
                        )}
                    </DialogTitle>
                    <Divider variant="fullWidth" />
                    <DialogContent>
                        <DialogContentText id="unlink-tax-payer-dialog-description">
                            {t(
                                "views.fulfilment_detail.components.fulfilment_detail_footer.unlink_fulfilment_dialog.title.content",
                            )}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            disabled={loading}
                            onClick={onClickAbortDialogs}
                            variant="outlined"
                        >
                            {t(
                                "views.fulfilment_detail.components.fulfilment_detail_footer.unlink_fulfilment_dialog.actions.abort",
                            )}
                        </Button>
                        <Button
                            disabled={loading}
                            onClick={async () => {
                                await onClickUnlinkTaxPayerButton();
                                setIsOpenUnlinkTaxPayerDialog(false);
                            }}
                            variant="contained"
                        >
                            {t(
                                "views.fulfilment_detail.components.fulfilment_detail_footer.unlink_fulfilment_dialog.actions.confirm",
                            )}
                            {loading && (
                                <CircularProgress
                                    color="inherit"
                                    sx={{ ml: 1 }}
                                    size={16}
                                />
                            )}
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog
                    open={isOpenCloseFulfilmentDialog}
                    onClose={onClickAbortDialogs}
                    aria-labelledby="close-fulfilment-dialog-title"
                    aria-describedby="close-fulfilment-dialog-description"
                >
                    <DialogTitle id="close-fulfilment-dialog-title">
                        {t(
                            "views.fulfilment_detail.components.fulfilment_detail_footer.close_fulfilment_dialog.title",
                        )}
                    </DialogTitle>
                    <Divider variant="fullWidth" />
                    <DialogContent>
                        <DialogContentText id="close-fulfilment-dialog-description">
                            {t(
                                "views.fulfilment_detail.components.fulfilment_detail_footer.close_fulfilment_dialog.title.content",
                            )}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            disabled={loading}
                            onClick={onClickAbortDialogs}
                            variant="outlined"
                        >
                            {t(
                                "views.fulfilment_detail.components.fulfilment_detail_footer.close_fulfilment_dialog.actions.abort",
                            )}
                        </Button>
                        <Button
                            disabled={loading}
                            onClick={async () => {
                                await onClickCloseFulfilmentButton();
                                setIsOpenCloseFulfilmentDialog(false);
                            }}
                            variant="contained"
                        >
                            {t(
                                "views.fulfilment_detail.components.fulfilment_detail_footer.close_fulfilment_dialog.actions.confirm",
                            )}
                            {loading && (
                                <CircularProgress
                                    color="inherit"
                                    sx={{ ml: 1 }}
                                    size={16}
                                />
                            )}
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog
                    open={isOpenReopenFulfilmentDialog}
                    onClose={onClickAbortDialogs}
                    aria-labelledby="reopen-fulfilment-dialog-title"
                    aria-describedby="reopen-fulfilment-dialog-description"
                >
                    <DialogTitle id="reopen-fulfilment-dialog-title">
                        {t(
                            "views.fulfilment_detail.components.fulfilment_detail_footer.reopen_fulfilment_dialog.title",
                        )}
                    </DialogTitle>
                    <Divider variant="fullWidth" />
                    <DialogContent>
                        <DialogContentText id="reopen-fulfilment-dialog-description">
                            {t(
                                "views.fulfilment_detail.components.fulfilment_detail_footer.reopen_fulfilment_dialog.title.content",
                            )}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            disabled={loading}
                            onClick={onClickAbortDialogs}
                            variant="outlined"
                        >
                            {t(
                                "views.fulfilment_detail.components.fulfilment_detail_footer.reopen_fulfilment_dialog.actions.abort",
                            )}
                        </Button>
                        <Button
                            disabled={loading}
                            onClick={async () => {
                                await onClickReopenFulfilmentButton();
                                setIsOpenReopenFulfilmentDialog(false);
                            }}
                            variant="contained"
                        >
                            {t(
                                "views.fulfilment_detail.components.fulfilment_detail_footer.reopen_fulfilment_dialog.actions.confirm",
                            )}
                            {loading && (
                                <CircularProgress
                                    color="inherit"
                                    sx={{ ml: 1 }}
                                    size={16}
                                />
                            )}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Stack>
        </Stack>
    );
};
